import React, { FC, useState } from 'react';

import cn from 'classnames';
import { Control, Controller } from 'react-hook-form';
import SelectComponent, { InputActionMeta } from 'react-select';
import { twMerge } from 'tailwind-merge';

import { FromItemHigligtedColor, Option } from 'interfaces';

import { FormLabel } from './index';

export type ExternalMultiSelectProps = {
  control: Control;
  name: string;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  className?: string;
  highlighted?: boolean;
  label?: string;
  labelClassName?: string;
  onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
  validation?: any;
  closeMenu?: boolean;
  isLoading?: boolean;
  fetchAllCallback?: () => Promise<Option[]>;
};
const highlightedColor = FromItemHigligtedColor.Blue;

const ExternalMultiSelect: FC<ExternalMultiSelectProps> = ({
  control,
  name,
  options,
  placeholder = 'Select options',
  disabled,
  isClearable,
  isSearchable = true,
  highlighted,
  label,
  labelClassName,
  onInputChange,
  className,
  validation,
  closeMenu,
  isLoading,
  fetchAllCallback,
  ...rest
}) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  return (
    <Controller
      control={control}
      name={name}
      rules={validation}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => {
        const selectedValues: Option[] = Array.isArray(value) ? value : [];
        const selectAllOption: Option = { value: '__select_all__', label: 'Select All' };

        // If loading, don't include the "Select All" option.
        const mergedOptions = isLoading
          ? options
          : Array.isArray(options) && options.length > 0
          ? [selectAllOption, ...options]
          : options;

        // Handler to remove an option from the selected array.
        const handleRemove = (optionToRemove: Option) => {
          const newSelected = selectedValues.filter((opt) => opt.value !== optionToRemove.value);
          onChange(newSelected);
        };

        // Custom react-select components to hide the internal multi-value display.
        const customComponents = {
          MultiValue: () => null,
          MultiValueContainer: () => null,
          MultiValueLabel: () => null,
          MultiValueRemove: () => null,
        };

        return (
          <div className={twMerge('external-multi-select', className)}>
            <div
              className={`flex justify-between items-center  ${
                selectedValues.length > 0 ? 'mb-2 border-b border-gray-300' : ''
              }`}
            >
              {typeof label === 'string' ? <FormLabel className={labelClassName}>{label}</FormLabel> : label}

              {selectedValues.length > 0 && (
                <div className='relative inline-block'>
                  <div
                    style={{ height: 'calc(100% + 1px)' }}
                    className='absolute inset-0 bg-white border-t border-r border-l border-gray-300 rounded-tr-md rounded-tl-md z-0'
                  />

                  <button
                    type='button'
                    onClick={() => onChange([])}
                    className='relative z-10 px-3 py-2 text-sm text-red-600 hover:text-red-800'
                  >
                    Remove All
                  </button>
                </div>
              )}
            </div>

            <div className='selected-options mb-2 flex flex-col gap-2 max-h-[400px] overflow-y-auto'>
              {selectedValues.map((selected) => (
                <div
                  key={selected.value}
                  className='selected-option flex items-center justify-between px-2 py-1 bg-violet-100 rounded'
                >
                  <span>{selected.label}</span>
                  <button
                    type='button'
                    onClick={() => handleRemove(selected)}
                    className='text-red-600 hover:text-red-800 ml-2'
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <SelectComponent
              isMulti
              options={mergedOptions}
              placeholder={isLoading ? 'Loading...' : placeholder}
              isDisabled={disabled}
              isClearable={isClearable}
              isSearchable={isSearchable}
              closeMenuOnSelect={closeMenu ? false : true}
              menuIsOpen={menuIsOpen}
              onMenuOpen={() => setMenuIsOpen(true)}
              onMenuClose={() => setMenuIsOpen(false)}
              isLoading={isLoading}
              filterOption={(candidate, input) =>
                candidate.data.value === '__select_all__' || candidate.label.toLowerCase().includes(input.toLowerCase())
              }
              noOptionsMessage={() => (isLoading ? 'Loading...' : 'No options')}
              className={cn('select-input div-py-0', {
                'select-input-error': error,
                [`select-input-highlighted-${highlightedColor}`]: !error && highlighted,
                '[&>div]:!bg-grey-100 [&>div]:!border-grey-200 [&_div]:!text-grey-300': disabled,
              })}
              onChange={(selectedOption: any) => {
                if (!Array.isArray(selectedOption)) {
                  onChange(selectedOption);
                  return;
                }

                const hasSelectAll = selectedOption.some((opt: Option) => opt.value === selectAllOption.value);
                const onlyRealPlusSelectAll =
                  mergedOptions.length === 2 && mergedOptions.some((o) => o.value === selectAllOption.value);

                // 1) If they clicked Select All, do your full‑fetch and close:
                if (hasSelectAll) {
                  setMenuIsOpen(false);
                  if (fetchAllCallback) {
                    fetchAllCallback().then((allOptions) => onChange(allOptions));
                  } else {
                    onChange(options);
                  }
                  return;
                }

                // 2) If there was only one real option + the “Select All” stub,
                //    and they just picked that one real option, close the menu:
                if (!hasSelectAll && onlyRealPlusSelectAll && selectedOption.length === 1) {
                  setMenuIsOpen(false);
                }

                // 3) Normal multi‑select update:
                onChange(selectedOption);
              }}
              onInputChange={(inputValue, actionMeta) => {
                if (onInputChange) {
                  onInputChange(inputValue, actionMeta);
                }
              }}
              value={selectedValues}
              ref={ref}
              components={customComponents}
              {...rest}
            />

            {error && <div className='text-red-600 text-sm mt-1'>{error.message}</div>}
          </div>
        );
      }}
    />
  );
};

export default ExternalMultiSelect;
